import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchMyConnections } from "../../redux/actions/connections";
import OtherFollowers from './OtherFollowers';
import OtherFollowing from './OtherFollowing';
import { setFriendListPanelOpen } from '../../state/uiSlice';
import { toast } from 'react-toastify';
import { loadSeller } from '../../redux/actions/user';
import SharedRightSlideout from '../Layout/SharedRightSlideout';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const ConnectionsPanel = () => {
  const isFriendListPanelOpen = useSelector((state) => state.ui.isFriendListPanelOpen);
  const [activeTab, setActiveTab] = useState('Followers');
  const dispatch = useDispatch();

  const currentProfile = useSelector((state) => state.user.currentProfile);
  const { user } = useSelector((state) => state.user);
  const seller = useSelector((state) => state.seller.activeSeller);
  const profileId = currentProfile === 'User' ? user?._id : seller?._id;
  const profileType = currentProfile === 'User' ? 'User' : 'Shop';

  const {
    hasFetchedMyFollowers,
    hasFetchedMyFollowing,
    myFollowers,
    myFollowing,
    isLoadingMyFollowers,
    isLoadingMyFollowing,
    error,
  } = useSelector((state) => state.connections);

  useEffect(() => {
    if (currentProfile !== 'User' && !seller) {
      dispatch(loadSeller());
    }
  }, [dispatch, currentProfile, seller]);

  const memoizedFollowers = useMemo(() => myFollowers, [myFollowers]);
  const memoizedFollowing = useMemo(() => myFollowing, [myFollowing]);

  useEffect(() => {
    if (!profileId || !profileType) return;

    if (activeTab === 'Followers' && !hasFetchedMyFollowers) {
      dispatch(fetchMyConnections(profileId, profileType, 'followers'))
        .catch((err) => {
          console.error('Error fetching followers:', err);
          toast.error('Failed to fetch followers.');
        });
    } else if (activeTab === 'Following' && !hasFetchedMyFollowing) {
      dispatch(fetchMyConnections(profileId, profileType, 'following'))
        .catch((err) => {
          console.error('Error fetching following:', err);
          toast.error('Failed to fetch following.');
        });
    }
  }, [dispatch, profileId, profileType, activeTab, hasFetchedMyFollowers, hasFetchedMyFollowing]);

  const isLoadingData = activeTab === 'Followers' ? isLoadingMyFollowers : isLoadingMyFollowing;
  const hasFetchedData = activeTab === 'Followers' ? hasFetchedMyFollowers : hasFetchedMyFollowing;
  const data = activeTab === 'Followers' ? memoizedFollowers : memoizedFollowing;

  const tabs = [
    { name: 'Followers', current: activeTab === 'Followers' },
    { name: 'Following', current: activeTab === 'Following' },
  ];

  const handleClose = () => {
    dispatch(setFriendListPanelOpen(false));
  };

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
  };

  // If the panel is not open, render nothing
  if (!isFriendListPanelOpen) {
    return null;
  }

  return (
    <SharedRightSlideout
      isOpen={isFriendListPanelOpen}
      onClose={handleClose}
      title="Social Connections"
      className="connections-shared-slideout"
      bodyClassName="connections-shared-slideout-body"
    >
      <div className="connections-panel-tabs-shell">
        <div className="connections-panel-tabs">
          <nav className="connections-panel-tabs-nav" aria-label="Social connection tabs">
            {tabs.map((tab) => (
              <button
                type="button"
                key={tab.name}
                className={classNames(
                  tab.current ? 'connections-panel-tab-active' : 'connections-panel-tab-inactive',
                  'connections-panel-tab'
                )}
                aria-current={tab.current ? 'page' : undefined}
                onClick={() => handleTabClick(tab.name)}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>
      <div className="connections-panel-content-body">
        {isLoadingData && (
          <div className="connections-panel-loading">
            <p>Loading {activeTab}...</p>
          </div>
        )}
        {error && (
          <div className="connections-panel-error">
            <p>Error: {error}</p>
          </div>
        )}
        {!isLoadingData && !error && hasFetchedData && data.length === 0 && (
          <div className="connections-panel-no-data">
            <p>No {activeTab.toLowerCase()} found.</p>
          </div>
        )}
        {!isLoadingData && !error && hasFetchedData && data.length > 0 && (
          <>
            {activeTab === 'Followers' && (
              <OtherFollowers
                followers={memoizedFollowers}
                isLoading={isLoadingMyFollowers}
                error={error}
              />
            )}
            {activeTab === 'Following' && (
              <OtherFollowing
                following={memoizedFollowing}
                isLoading={isLoadingMyFollowing}
                error={error}
              />
            )}
          </>
        )}
      </div>
    </SharedRightSlideout>
  );
};

export default ConnectionsPanel;
